import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {InventoryValidationErrorLabel} from 'inventory/InventoryConstants';
import {getValidationSearchUrl} from 'inventory/location-validation/ParentStorableValidationErrors';
import {inventoryValidationErrorLabelToDisplayText} from 'inventory/location-validation/ParentStorableValidationUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

export interface StorableSchemaValidationTableProps {
  registryId: string;
  storablesWithErrorInfo: Array<StorableWithValidationInfo>;
}

export interface ErrorInfoWithRelatedItems {
  hasError: boolean;
  relatedItemBarcodes: Array<string>;
  /** This number may be greater than the length of relatedItemBarcodes; it intentionally includes all
   * inaccessible or otherwise invalid items as well. */
  totalNumberOfRelatedItems: number;
}

export interface StorableWithValidationInfo {
  id: string;
  name: string;
  barcode: string;
  hasCapacityError: boolean;
  nonLocationsDisallowedError: ErrorInfoWithRelatedItems;
  itemTypesDisallowedError: ErrorInfoWithRelatedItems;
}

interface ValidationTableRowData {
  storable: JSX.Element;
  overCapacity: JSX.Element | string | null;
  nonLocationsDisallowed: JSX.Element | string | null;
  itemTypesDisallowed: JSX.Element | string | null;
}

interface StorableValidationTableRow extends ScrollableTableRow {
  data: ValidationTableRowData;
  extra: {storable: StorableWithValidationInfo};
}

type StorableValidationRowKey = keyof ValidationTableRowData;

/**
 * Returns element with total item count & link, handling inaccessible items.
 *
 * Inaccessible items are assumed not to be linkable, and so accessible item count and the link is separated
 * out when inaccessible items are present.
 * @param totalItemCount Total number of items
 * @param accessibleItemsHref Link to use for the accessible items
 * @param accessibleItemCount Count of accessible items
 * @param itemLabel Label to use for "items"
 */
export function getItemCountWithAccessibleItemLinks({
  totalItemCount,
  accessibleItemsHref,
  accessibleItemCount,
  itemLabel,
}: {
  totalItemCount: number;
  accessibleItemCount: number;
  accessibleItemsHref: string;
  itemLabel: string;
}): JSX.Element {
  if (accessibleItemCount === totalItemCount) {
    return <a href={accessibleItemsHref}>{totalItemCount}</a>;
  }

  return (
    <span>
      {`${totalItemCount} `}
      {accessibleItemCount > 0 ? (
        <a href={accessibleItemsHref}>{`(View ${accessibleItemCount} accessible ${pluralize(
          accessibleItemCount,
          itemLabel
        )})`}</a>
      ) : (
        `(No accessible ${pluralize(2, itemLabel)})`
      )}
    </span>
  );
}

export default function StorableSchemaValidationTable(
  props: StorableSchemaValidationTableProps
): JSX.Element | null {
  const getStorableValidationSortValue = (
    row: StorableValidationTableRow,
    sortKey: StorableValidationRowKey
  ): string | number => {
    switch (sortKey) {
      case 'storable':
        return row.extra.storable.name;
      case 'overCapacity':
        return Number(row.extra.storable.hasCapacityError);
      case 'nonLocationsDisallowed':
        return Number(row.extra.storable.nonLocationsDisallowedError.totalNumberOfRelatedItems);
      case 'itemTypesDisallowed':
        return Number(row.extra.storable.itemTypesDisallowedError.totalNumberOfRelatedItems);
      default:
        return row.data[sortKey];
    }
  };

  const getErrorWithRelatedItemsDisplay = (
    error: ErrorInfoWithRelatedItems,
    storableId: string
  ): JSX.Element | null => {
    if (!error.hasError) {
      return null;
    }
    return getItemCountWithAccessibleItemLinks({
      totalItemCount: error.totalNumberOfRelatedItems,
      accessibleItemCount: error.relatedItemBarcodes.length,
      accessibleItemsHref: getValidationSearchUrl(error.relatedItemBarcodes, props.registryId, storableId),
      itemLabel: 'item',
    });
  };

  const rows: Array<StorableValidationTableRow> = props.storablesWithErrorInfo.map((storable, idx) => {
    const data = {
      storable: (
        <a href={getValidationSearchUrl([], props.registryId, storable.id)}>{`${storable.name}${
          storable.barcode !== storable.name ? ` (${storable.barcode})` : ''
        }`}</a>
      ),
      overCapacity: storable.hasCapacityError ? <BenchlingIcon icon="check" /> : null,
      nonLocationsDisallowed: getErrorWithRelatedItemsDisplay(
        storable.nonLocationsDisallowedError,
        storable.id
      ),
      itemTypesDisallowed: getErrorWithRelatedItemsDisplay(storable.itemTypesDisallowedError, storable.id),
    };
    return {
      id: storable.id,
      data,
      extra: {storable},
      rowOnlyClassName: classNames('storableSchemaValidation-row', {
        'storableSchemaValidation-lastRow': props.storablesWithErrorInfo.length - 1 === idx,
      }),
    };
  });

  const columns: Array<ScrollableTableColumn> = [
    {
      header: 'Location',
      accessorKey: 'storable',
      isSortable: true,
      sortKey: 'storable',
      width: 165,
      isFixed: true,
    },
    {
      header: inventoryValidationErrorLabelToDisplayText(InventoryValidationErrorLabel.OVER_CAPACITY, false),
      accessorKey: 'overCapacity',
      isSortable: true,
      sortKey: 'overCapacity',
      flexGrow: 1,
    },
    {
      header: inventoryValidationErrorLabelToDisplayText(
        InventoryValidationErrorLabel.NON_LOCATION_ITEMS_DISALLOWED,
        false
      ),
      accessorKey: 'nonLocationsDisallowed',
      isSortable: true,
      sortKey: 'nonLocationsDisallowed',
      flexGrow: 1,
    },
    {
      header: inventoryValidationErrorLabelToDisplayText(
        InventoryValidationErrorLabel.ITEM_TYPES_DISALLOWED,
        false
      ),
      accessorKey: 'itemTypesDisallowed',
      isSortable: true,
      sortKey: 'itemTypesDisallowed',
      flexGrow: 1,
    },
  ];

  return rows.length > 0 ? (
    <SortableScrollableTable
      canResizeColumns={true}
      isCompact={false}
      disableSelection={true}
      columns={columns}
      className="storableSchemaValidation"
      initialRows={rows}
      initialSort={[]}
      getSortValue={getStorableValidationSortValue}
      tableKey={TABLE_KEYS.STORABLE_SCHEMA_VALIDATION_TABLE}
    />
  ) : null;
}
